import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigation } from '../hooks/useNavigation';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Plus,
  Receipt,
  ClipboardList,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  CheckCircle,
  AlertCircle,
  Info,
  Loader2,
  Tag,
  Box,
  Package2,
  Layers,
  Calculator,
  Phone,
  Mail,
  MapPin,
  Star,
  UserPlus,
  UserCheck,
  Building2,
  UserX,
  Home
} from 'lucide-react';
import toast from 'react-hot-toast';

const CustomerList = () => {
  
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { formatCurrency } = useSettings();
  const { navigationItems } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Customer state management
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerStats, setCustomerStats] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: '',
    sort_by: 'name',
    sort_order: 'asc'
  });

  // Add Customer Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addCustomerLoading, setAddCustomerLoading] = useState(false);
  const [addCustomerForm, setAddCustomerForm] = useState({
    name: '',
    customerID: '',
    email: '',
    phone: '',
    address: '',
    status: '',
    is_wholesale: false,
    tax_number: '',
    contact_person: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Add validation state for customer name and email
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidatingName, setIsValidatingName] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [fieldValidation, setFieldValidation] = useState({
    name: { isValid: null, message: '' },
    email: { isValid: null, message: '' }
  });

  // Customer action modals state
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteCustomerLoading, setDeleteCustomerLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);


  const handleCardClick = useCallback((href) => {
    navigate(href);
  }, [navigate, location.pathname]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  // Validation functions
  const validateCustomerName = useCallback(async (name) => {
    if (!name || name.trim().length < 2) {
      setFieldValidation(prev => ({
        ...prev,
        name: { isValid: false, message: 'Customer name must be at least 2 characters long' }
      }));
      return;
    }

    setIsValidatingName(true);
    try {
      const excludeId = isEditMode ? selectedCustomer?.id : null;
      const response = await apiService.validateCustomerName(name, excludeId);
      
      setFieldValidation(prev => ({
        ...prev,
        name: {
          isValid: response.available,
          message: response.message
        }
      }));
    } catch (error) {
      console.error('Error validating customer name:', error);
      setFieldValidation(prev => ({
        ...prev,
        name: { isValid: false, message: 'Error validating customer name' }
      }));
    } finally {
      setIsValidatingName(false);
    }
  }, [isEditMode, selectedCustomer]);

  const validateCustomerEmail = useCallback(async (email) => {
    if (!email) {
      setFieldValidation(prev => ({
        ...prev,
        email: { isValid: null, message: '' }
      }));
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFieldValidation(prev => ({
        ...prev,
        email: { isValid: false, message: 'Please enter a valid email address' }
      }));
      return;
    }

    setIsValidatingEmail(true);
    try {
      const excludeId = isEditMode ? selectedCustomer?.id : null;
      const response = await apiService.validateCustomerEmail(email, excludeId);
      
      setFieldValidation(prev => ({
        ...prev,
        email: {
          isValid: response.available,
          message: response.message
        }
      }));
    } catch (error) {
      console.error('Error validating customer email:', error);
      setFieldValidation(prev => ({
        ...prev,
        email: { isValid: false, message: 'Error validating email address' }
      }));
    } finally {
      setIsValidatingEmail(false);
    }
  }, [isEditMode, selectedCustomer]);

  // Debounced validation calls
  const debouncedValidateName = useCallback(
    debounce((name) => {
      validateCustomerName(name);
    }, 500),
    [validateCustomerName]
  );

  const debouncedValidateEmail = useCallback(
    debounce((email) => {
      validateCustomerEmail(email);
    }, 500),
    [validateCustomerEmail]
  );

  // Helper function for debounce
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Add Customer Modal handlers
  // Generate Customer ID with sequential numbering per day
  const generateCustomerId = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateString = `${year}${month}${day}`;
    
    try {
      // Get the count of customers created today
      const response = await apiService.getCustomers(`?created_today=true`);
      const todayCustomers = response.customers || [];
      const todayCount = todayCustomers.length;
      const nextNumber = String(todayCount + 1).padStart(3, '0');
      
      return `CUST-${dateString}${nextNumber}`;
    } catch (error) {
      console.error('Error generating customer ID:', error);
      // Fallback to random if API fails
      const random = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
      return `CUST-${dateString}${random}`;
    }
  };

  const handleAddCustomer = useCallback(async () => {
    setShowAddModal(true);
    setIsEditMode(false);
    setSelectedCustomer(null);
    
    // Generate customer ID
    const customerID = await generateCustomerId();
    
    setAddCustomerForm({
      name: '',
      customerID: customerID,
      email: '',
      phone: '',
      address: '',
      status: '',
      is_wholesale: false,
      tax_number: '',
      contact_person: '',
      notes: ''
    });
    setFormErrors({});
    setValidationErrors({});
  }, []);

  // Function to refresh modal with new Customer ID
  const refreshModal = useCallback(async () => {
    const customerID = await generateCustomerId();
    
    setAddCustomerForm({
      name: '',
      customerID: customerID,
      email: '',
      phone: '',
      address: '',
      status: '',
      is_wholesale: false,
      tax_number: '',
      contact_person: '',
      notes: ''
    });
    setFormErrors({});
    setValidationErrors({});
    setFieldValidation({
      name: { isValid: null, message: '' },
      email: { isValid: null, message: '' }
    });
    setIsValidatingName(false);
    setIsValidatingEmail(false);
  }, []);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        search: filters.search,
        status: filters.status,
        type: filters.type,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      });

      const response = await apiService.getCustomers(params.toString());
      const data = response.data || response;
      
      setCustomers(data.customers || []);
      setPagination(prev => ({
        ...prev,
        totalPages: data.last_page || 1,
        totalItems: data.total || 0
      }));
      
      // Calculate stats
      const stats = {
        total_customers: data.total || 0,
        active_customers: data.customers?.filter(c => c.status === 'active').length || 0,
        wholesale_customers: data.customers?.filter(c => c.is_wholesale).length || 0,
        total_balance: data.customers?.reduce((sum, c) => sum + parseFloat(c.balance || 0), 0) || 0
      };
      setCustomerStats(stats);
    } catch (error) {
      console.error('❌ CustomerList - Error fetching customers:', error);
      console.error('❌ CustomerList - Error details:', error.message);
      console.error('❌ CustomerList - Error stack:', error.stack);
      toast.error('Failed to fetch customers: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, filters]);

  // Form submission handler
  const handleAddCustomerSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormErrors({});
    setValidationErrors({});
    
    // Basic validation
    if (!addCustomerForm.name.trim()) {
      setFormErrors({ name: 'Customer name is required' });
      return;
    }
    
    if (!addCustomerForm.phone.trim()) {
      setFormErrors({ phone: 'Phone number is required' });
      return;
    }
    
    if (!addCustomerForm.status) {
      setFormErrors({ status: 'Status is required' });
      return;
    }

    // Check field validation results
    if (fieldValidation.name.isValid === false) {
      setFormErrors({ name: fieldValidation.name.message });
      return;
    }

    if (fieldValidation.email.isValid === false) {
      setFormErrors({ email: fieldValidation.email.message });
      return;
    }

    // Check if validation is still in progress
    if (isValidatingName || isValidatingEmail) {
      toast.error('Please wait for validation to complete');
      return;
    }

    setAddCustomerLoading(true);
    setFormErrors({});

    try {
      
      let response;
      if (isEditMode) {
        // Update existing customer - include customerID for edit mode
        response = await apiService.updateCustomer(selectedCustomer.id, addCustomerForm);
        if (response.success) {
          toast.success('Customer updated successfully!');
          await fetchCustomers();
          // Close modal after successful update
          setShowAddModal(false);
        } else {
          toast.error(response.message || 'Failed to update customer');
        }
      } else {
        // Add new customer - exclude customerID as backend generates it
        const { customerID, ...customerData } = addCustomerForm;
        response = await apiService.addCustomer(customerData);
        if (response.success) {
          toast.success('Customer added successfully!');
          await fetchCustomers();
          // Refresh modal with new Customer ID instead of closing
          await refreshModal();
        } else {
          toast.error(response.message || 'Failed to add customer');
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} customer:`, error);
      
      // Try to parse error response for validation errors
      try {
        const errorResponse = await error.response?.json();
        if (errorResponse?.errors) {
          setValidationErrors(errorResponse.errors);
          toast.error('Please fix the validation errors below');
        } else {
          toast.error(`Failed to ${isEditMode ? 'update' : 'add'} customer: ` + error.message);
        }
      } catch (parseError) {
        toast.error(`Failed to ${isEditMode ? 'update' : 'add'} customer: ` + error.message);
      }
    } finally {
      setAddCustomerLoading(false);
    }
  }, [addCustomerForm, fetchCustomers, refreshModal]);

  const handleViewCustomer = useCallback((customer) => {
    setSelectedCustomer(customer);
    setShowViewModal(true);
  }, []);

  const handleEditCustomer = useCallback((customer) => {
    setSelectedCustomer(customer);
    setIsEditMode(true);
    setAddCustomerForm({
      name: customer.name || '',
      customerID: customer.customerID || '', // Use existing Customer ID
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      status: customer.status || '',
      is_wholesale: customer.is_wholesale || false,
      tax_number: customer.tax_number || '',
      contact_person: customer.contact_person || '',
      notes: customer.notes || ''
    });
    setShowAddModal(true);
    setFormErrors({});
    setValidationErrors({});
    setFieldValidation({
      name: { isValid: null, message: '' },
      email: { isValid: null, message: '' }
    });
    setIsValidatingName(false);
    setIsValidatingEmail(false);
  }, []);

  const handleDeleteCustomer = useCallback((customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedCustomer) return;

    setDeleteCustomerLoading(true);
    try {
      const response = await apiService.deleteCustomer(selectedCustomer.id);
      
      if (response.success) {
        toast.success('Customer deleted successfully!');
        await fetchCustomers();
        setShowDeleteModal(false);
        setSelectedCustomer(null);
      } else {
        toast.error(response.message || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer: ' + error.message);
    } finally {
      setDeleteCustomerLoading(false);
    }
  }, [selectedCustomer, fetchCustomers]);

  const handleCloseModals = useCallback(() => {
    setShowAddModal(false);
    setShowViewModal(false);
    setShowDeleteModal(false);
    setSelectedCustomer(null);
    setAddCustomerForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      status: 'active',
      is_wholesale: false,
      credit_limit: '',
      payment_terms: '',
      tax_number: '',
      contact_person: '',
      notes: '',
      preferred_payment_method: 'cash',
      discount_percentage: ''
    });
    setFormErrors({});
    setValidationErrors({});
    setFieldValidation({
      name: { isValid: null, message: '' },
      email: { isValid: null, message: '' }
    });
    setIsValidatingName(false);
    setIsValidatingEmail(false);
  }, []);


  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Add error boundary check
  if (!user) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading...</h2>
        <p className="text-gray-600">Please wait while we load your customers.</p>
      </div>
    </div>;
  }


  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Active' },
      inactive: { color: 'bg-red-100 text-red-800', text: 'Inactive' },
      suspended: { color: 'bg-yellow-100 text-yellow-800', text: 'Suspended' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getTypeBadge = (isWholesale) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isWholesale 
          ? 'bg-blue-100 text-blue-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isWholesale ? 'Wholesale' : 'Retail'}
      </span>
    );
  };

  return (
    <Layout>
      <main className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-blue-50'} pt-20`}>
        <div className="max-w-7xl mx-auto p-6 space-y-8">
          {/* Page Header */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Users className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                        Customer Management
                      </h1>
                      <p className="text-gray-600 text-lg">Manage your customers and their information with ease</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={fetchCustomers}
                      disabled={loading}
                      className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    >
                      <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    <button 
                      onClick={() => navigate('/customer-payments')}
                      className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
                    >
                      <CreditCard size={20} />
                      Payments
                    </button>
                    <button 
                      onClick={handleAddCustomer}
                      className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Plus size={20} />
                      Add Customer
                    </button>
                  </div>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600 mb-1">Total Customers</p>
                      <p className="text-3xl font-bold text-blue-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : customerStats.total_customers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">Active Customers</p>
                      <p className="text-3xl font-bold text-green-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : customerStats.active_customers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                      <UserCheck className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600 mb-1">Wholesale</p>
                      <p className="text-3xl font-bold text-orange-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : customerStats.wholesale_customers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Package className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">Total Balance</p>
                      <p className="text-3xl font-bold text-red-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(customerStats.total_balance || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters and Search */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Filter className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Filters & Search</h2>
                    <p className="text-gray-600">Find and filter your customers</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                      type="text" 
                      placeholder="Search customers..." 
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                    />
                  </div>
                  <select 
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <select 
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="all">All Types</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="retail">Retail</option>
                  </select>
                  <select 
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="name">Sort by Name</option>
                    <option value="email">Sort by Email</option>
                    <option value="created_at">Sort by Date</option>
                    <option value="balance">Sort by Balance</option>
                  </select>
                  <select 
                    value={pagination.itemsPerPage}
                    onChange={(e) => {
                      setPagination(prev => ({ 
                        ...prev, 
                        itemsPerPage: parseInt(e.target.value),
                        currentPage: 1 
                      }));
                    }}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value={10}>10 per page</option>
                    <option value={15}>15 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2">
                    <Filter size={16} />
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Customers Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                              <span className="ml-2 text-gray-600">Loading customers...</span>
                            </div>
                          </td>
                        </tr>
                      ) : customers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center">
                            <div className="text-center">
                              <Users className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
                              <p className="mt-1 text-sm text-gray-500">
                                {filters.search || filters.status !== 'all' || filters.type !== 'all' 
                                  ? 'Try adjusting your search or filters.' 
                                  : 'Get started by adding your first customer.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        customers.map((customer) => (
                          <tr key={customer.id} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-blue-600">
                                      {customer.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {customer.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {customer.customerID}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{customer.email}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {customer.phone}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getTypeBadge(customer.is_wholesale)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(customer.status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(customer.balance || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewCustomer(customer)}
                                  className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="View"
                                >
                                  <Eye size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    View
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleEditCustomer(customer)}
                                  className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="Edit"
                                >
                                  <Edit size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Edit
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleDeleteCustomer(customer)}
                                  className="group/btn relative p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="Delete"
                                >
                                  <Trash2 size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Delete
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                        <span className="font-medium">{pagination.totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                          disabled={pagination.currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
        </div>
      </main>

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                    <Plus size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{isEditMode ? 'Edit Customer' : 'Add New Customer'}</h2>
                    <p className="text-white text-opacity-80 text-sm">
                      {isEditMode ? 'Update customer information' : 'Create a new customer in your system'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModals}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            <form onSubmit={handleAddCustomerSubmit} className="overflow-y-auto max-h-[calc(95vh-100px)]">
              <div className="p-6 space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                      <User size={14} className="text-blue-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Basic Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {/* Customer Name */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Name *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={addCustomerForm.name}
                          onChange={(e) => {
                            setAddCustomerForm(prev => ({ ...prev, name: e.target.value }));
                            debouncedValidateName(e.target.value);
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.name || fieldValidation.name.isValid === false 
                              ? 'border-red-500' 
                              : fieldValidation.name.isValid === true 
                                ? 'border-green-500' 
                                : 'border-gray-300'
                          }`}
                          placeholder="Enter customer name"
                          required
                        />
                        {isValidatingName && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                          </div>
                        )}
                        {fieldValidation.name.isValid === true && !isValidatingName && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <CheckCircle size={16} className="text-green-500" />
                          </div>
                        )}
                        {fieldValidation.name.isValid === false && !isValidatingName && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <X size={16} className="text-red-500" />
                          </div>
                        )}
                      </div>
                      {(formErrors.name || fieldValidation.name.message) && (
                        <p className={`text-xs mt-1 flex items-center ${
                          fieldValidation.name.isValid === true ? 'text-green-600' : 'text-red-500'
                        }`}>
                          {fieldValidation.name.isValid === true ? (
                            <CheckCircle size={12} className="mr-1" />
                          ) : (
                            <AlertCircle size={12} className="mr-1" />
                          )}
                          {formErrors.name || fieldValidation.name.message}
                        </p>
                      )}
                    </div>

                    {/* Customer ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer ID / Code *
                      </label>
                      <input
                        type="text"
                        value={addCustomerForm.customerID}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono text-sm"
                        placeholder={isEditMode ? "Existing Customer ID" : "Auto-generated"}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {isEditMode ? 'Existing Customer ID (cannot be changed)' : 'Auto-generated Customer ID'}
                      </p>
                    </div>

                    
                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status *
                      </label>
                      <select
                        value={addCustomerForm.status}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, status: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.status ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      {formErrors.status && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          {formErrors.status}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={addCustomerForm.phone}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="0241234567"
                        required
                      />
                      {formErrors.phone && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          {formErrors.phone}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={addCustomerForm.email}
                          onChange={(e) => {
                            setAddCustomerForm(prev => ({ ...prev, email: e.target.value }));
                            debouncedValidateEmail(e.target.value);
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.email || fieldValidation.email.isValid === false 
                              ? 'border-red-500' 
                              : fieldValidation.email.isValid === true 
                                ? 'border-green-500' 
                                : 'border-gray-300'
                          }`}
                          placeholder="customer@example.com"
                        />
                        {isValidatingEmail && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                          </div>
                        )}
                        {fieldValidation.email.isValid === true && !isValidatingEmail && addCustomerForm.email && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <CheckCircle size={16} className="text-green-500" />
                          </div>
                        )}
                        {fieldValidation.email.isValid === false && !isValidatingEmail && addCustomerForm.email && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <X size={16} className="text-red-500" />
                          </div>
                        )}
                      </div>
                      {(formErrors.email || fieldValidation.email.message) && (
                        <p className={`text-xs mt-1 flex items-center ${
                          fieldValidation.email.isValid === true ? 'text-green-600' : 'text-red-500'
                        }`}>
                          {fieldValidation.email.isValid === true ? (
                            <CheckCircle size={12} className="mr-1" />
                          ) : (
                            <AlertCircle size={12} className="mr-1" />
                          )}
                          {formErrors.email || fieldValidation.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                    {/* Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <textarea
                        value={addCustomerForm.address}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, address: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Enter customer address"
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={addCustomerForm.notes}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Additional notes about customer"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                      <Building2 size={14} className="text-green-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Business Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Customer Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Type
                      </label>
                      <select
                        value={addCustomerForm.is_wholesale ? 'wholesale' : 'retail'}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, is_wholesale: e.target.value === 'wholesale' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="retail">Retail Customer</option>
                        <option value="wholesale">Wholesale Customer</option>
                      </select>
                    </div>

                    {/* Tax Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax Number
                      </label>
                      <input
                        type="text"
                        value={addCustomerForm.tax_number}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, tax_number: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Tax identification number"
                      />
                    </div>

                    {/* Contact Person */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        value={addCustomerForm.contact_person}
                        onChange={(e) => setAddCustomerForm(prev => ({ ...prev, contact_person: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Primary contact person"
                      />
                    </div>
                  </div>
                </div>

              </div>

              {/* Form Actions */}
              <div className="bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModals}
                    className="px-5 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addCustomerLoading}
                    className="px-6 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addCustomerLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {isEditMode ? 'Updating...' : 'Adding...'}
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        {isEditMode ? 'Update Customer' : 'Add Customer'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Customer Modal */}
      {showViewModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedCustomer.name}</h2>
                    <p className="text-white text-opacity-80 text-sm">
                      Customer ID: {selectedCustomer.customerID}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModals}
                  className="p-2 bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 rounded-lg transition-colors"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(95vh-120px)]">
              <div className="p-6 space-y-6">
                {/* Customer Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-600">Status</p>
                        <div className="mt-1">
                          {getStatusBadge(selectedCustomer.status)}
                        </div>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${
                        selectedCustomer.status === 'active' ? 'bg-green-500' : 
                        selectedCustomer.status === 'inactive' ? 'bg-red-500' : 'bg-yellow-500'
                      }`}></div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-600">Customer Type</p>
                        <div className="mt-1">
                          {getTypeBadge(selectedCustomer.is_wholesale)}
                        </div>
                      </div>
                      {selectedCustomer.is_wholesale ? (
                        <Package className="h-6 w-6 text-green-500" />
                      ) : (
                        <User className="h-6 w-6 text-green-500" />
                      )}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-600">Balance</p>
                        <p className="text-lg font-bold text-orange-900">
                          {formatCurrency(selectedCustomer.balance || 0)}
                        </p>
                      </div>
                      <DollarSign className="h-6 w-6 text-orange-500" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-600">Member Since</p>
                        <p className="text-lg font-bold text-purple-900">
                          {selectedCustomer.created_at ? 
                            new Date(selectedCustomer.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short' 
                            }) : 'N/A'
                          }
                        </p>
                      </div>
                      <Calendar className="h-6 w-6 text-purple-500" />
                    </div>
                  </div>
                </div>

                {/* Detailed Information Sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Phone size={14} className="text-blue-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Email Address</p>
                          <p className="text-gray-900">{selectedCustomer.email || 'Not provided'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Phone Number</p>
                          <p className="text-gray-900">{selectedCustomer.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Address</p>
                          <p className="text-gray-900">{selectedCustomer.address || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                        <Building2 size={14} className="text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Customer Type</p>
                        <div className="mt-1">
                          {getTypeBadge(selectedCustomer.is_wholesale)}
                        </div>
                      </div>
                      
                      {selectedCustomer.tax_number && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Tax Number</p>
                          <p className="text-gray-900 font-mono">{selectedCustomer.tax_number}</p>
                        </div>
                      )}
                      
                      {selectedCustomer.contact_person && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Contact Person</p>
                          <p className="text-gray-900">{selectedCustomer.contact_person}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                {(selectedCustomer.notes || selectedCustomer.customerID) && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Info size={14} className="text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                    </div>
                    
                    <div className="space-y-4">
                      {selectedCustomer.customerID && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Customer ID</p>
                          <p className="text-gray-900 font-mono text-lg">{selectedCustomer.customerID}</p>
                        </div>
                      )}
                      
                      {selectedCustomer.notes && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Notes</p>
                          <p className="text-gray-900 bg-white p-3 rounded-lg border">{selectedCustomer.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BarChart3 size={14} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Quick Statistics</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedCustomer.status === 'active' ? '✓' : '✗'}
                      </p>
                      <p className="text-sm text-gray-600">Account Status</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {selectedCustomer.is_wholesale ? 'W' : 'R'}
                      </p>
                      <p className="text-sm text-gray-600">Customer Type</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {selectedCustomer.created_at ? 
                          Math.floor((new Date() - new Date(selectedCustomer.created_at)) / (1000 * 60 * 60 * 24)) : 0
                        }
                      </p>
                      <p className="text-sm text-gray-600">Days Active</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="bg-white border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Clock size={14} />
                  <span>Last updated: {selectedCustomer.updated_at ? 
                    new Date(selectedCustomer.updated_at).toLocaleString('en-US') : 'N/A'
                  }</span>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleCloseModals}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleEditCustomer(selectedCustomer);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 font-medium"
                  >
                    <Edit size={16} />
                    Edit Customer
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleDeleteCustomer(selectedCustomer);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2 font-medium"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Customer Modal */}
      {showDeleteModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 text-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                    <Trash2 size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Delete Customer</h2>
                    <p className="text-white text-opacity-80 text-sm">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModals}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle size={24} className="text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Are you sure you want to delete this customer?
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Name:</span>
                        <p className="text-gray-900">{selectedCustomer.name}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Customer ID:</span>
                        <p className="text-gray-900 font-mono">{selectedCustomer.customerID}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Email:</span>
                        <p className="text-gray-900">{selectedCustomer.email || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Phone:</span>
                        <p className="text-gray-900">{selectedCustomer.phone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    This will permanently delete the customer and all associated data. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCloseModals}
                  className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteCustomerLoading}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteCustomerLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Customer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CustomerList;